import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react';
import { isNativeShell } from '@mlm/ui/lib/native-shell';
import { CoachButton, ErrorNote, Eyebrow, Loading } from '../components/primitives';
import { MethodOption } from '../components/pay-options';
import { coaching, fmtPeso, performGatewayHandoff, store, type CreditEntry } from '../lib/api';
import { instantDay } from '../lib/format';
import { qk, useEntitlement } from '../lib/queries';

/**
 * Credits (2026-10-13): what the member has to spend on Master Kala and the
 * daily texts, where it came from, what each action costs, and the packs on
 * sale when it runs out. The ₱250 month includes a bundle that lasts the
 * month; bought credits never expire; nothing in the app is free.
 */

const KIND_COPY: Record<string, string> = {
  included_grant: 'Included with your month',
  included_expired: 'Month ended, unused included credits',
  topup: 'Credits bought',
  product_grant: 'Credits bought',
  plan_reward: 'Credits earned',
  admin_adjust: 'Adjusted by support',
  refund: 'Refunded',
  backfill: 'Carried over',
  spend: 'Used',
};

const ACTION_COPY: Record<string, string> = {
  kala_message: 'Master Kala',
  blueprint: 'Success blueprint',
  daily_plan: 'Daily plan',
  guidance: 'Daily guidance',
  ritual_evening: 'Evening review',
  weekly_review: 'Weekly review',
  journal_reflection: 'Journal reflection',
  affirmation: 'Affirmation',
  action_suggestions: 'Action suggestions',
  alignment_todos: 'Alignment to-dos',
  aura: 'Aura reading',
  karma: 'Karma reading',
  palmistry: 'Palm reading',
  assistant: 'Assistant',
};

function entryLabel(e: CreditEntry): string {
  if (e.kind === 'spend') return ACTION_COPY[e.action ?? ''] ?? 'Used';
  return KIND_COPY[e.kind] ?? e.kind.replace(/_/g, ' ');
}

export function CreditsPage() {
  const navigate = useNavigate();
  const entitlement = useEntitlement();
  const entries = useQuery({ queryKey: qk.creditEntries, queryFn: coaching.creditEntries });
  const methods = useQuery({ queryKey: qk.paymentMethods, queryFn: store.paymentMethods });
  const [packId, setPackId] = useState<string | null>(null);
  const [methodCode, setMethodCode] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const credits = entitlement.data?.credits ?? null;
  const packs = credits?.packs ?? [];
  const pack = packs.find((p) => p.productId === packId) ?? packs[0] ?? null;
  const payable = (methods.data?.methods ?? []).filter(
    (m) =>
      m.virtualEnabled &&
      m.currency === 'cash' &&
      m.settlement !== 'cod_manual' &&
      (m.settlement !== 'wallet' || m.balance !== null),
  );
  const method =
    payable.find((m) => m.code === methodCode) ?? (payable.length === 1 ? payable[0] : null);

  const buy = useMutation({
    mutationFn: async () => {
      const order = await store.createOrder({
        productId: pack!.productId,
        paymentMethodCode: method!.code,
      });
      if (method!.settlement === 'gateway') {
        performGatewayHandoff(await store.initiateGateway(order.id));
        return null;
      }
      return order.id;
    },
    onSuccess: (orderId) => {
      if (orderId) navigate(`/orders/${orderId}`, { replace: true });
    },
    onError: setError,
  });

  if (!entitlement.data) return <Loading />;

  return (
    <div className="mx-auto w-full max-w-xl px-5 pb-16 pt-6 lg:max-w-3xl lg:pt-10">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-sm text-sage"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden /> Back
      </button>
      <Eyebrow className="mt-6">Credits</Eyebrow>
      <h1 className="mt-2 font-display text-3xl text-ink">
        {credits ? credits.total : 0}{' '}
        <span className="font-sans text-base font-normal text-sage">
          {credits?.total === 1 ? 'credit' : 'credits'} to spend
        </span>
      </h1>
      {credits && (
        <p className="mt-2 text-[0.93rem] leading-relaxed text-ink/70">
          {credits.included > 0
            ? `${credits.included} included with your month${
                credits.includedUntil ? `, until ${instantDay(credits.includedUntil)}` : ''
              }`
            : 'Nothing left from this month’s included credits'}
          {credits.purchased > 0
            ? `, plus ${credits.purchased} you bought that never expire.`
            : '.'}
        </p>
      )}

      {credits && credits.costs.length > 0 && (
        <section className="mt-8">
          <Eyebrow className="mb-3">What things cost</Eyebrow>
          <ul className="divide-y divide-border border-y border-border">
            {credits.costs
              .filter((c) => c.cost > 0)
              .map((c) => (
                <li key={c.key} className="flex items-baseline justify-between gap-4 py-2.5">
                  <span className="text-[0.93rem] text-ink/85">{c.label}</span>
                  <span className="font-display text-ink">
                    {c.cost} {c.cost === 1 ? 'credit' : 'credits'}
                  </span>
                </li>
              ))}
          </ul>
          <p className="eyebrow mt-2">
            Your included credits are used first. A credit is only spent when Kala actually answers.
          </p>
        </section>
      )}

      <section className="mt-10">
        <Eyebrow className="mb-3">Get more</Eyebrow>
        {packs.length === 0 ? (
          <p className="text-[0.93rem] text-ink/70">
            No credit packs are on sale right now. Please check back soon.
          </p>
        ) : isNativeShell() ? (
          <>
            <ul className="divide-y divide-border border-y border-border">
              {packs.map((p) => (
                <li key={p.productId} className="flex items-baseline justify-between gap-4 py-2.5">
                  <span className="text-[0.93rem] text-ink/85">{p.name}</span>
                  <span className="font-display text-ink">{fmtPeso(p.price)}</span>
                </li>
              ))}
            </ul>
            <a
              href={`${window.location.origin.replace(/^https?:\/\/app\./, 'https://')}/credits`}
              target="_blank"
              rel="noopener"
              className="mt-4 block"
            >
              <CoachButton kind="gold" block>
                Buy credits on the website
              </CoachButton>
            </a>
            <p className="eyebrow mt-2 text-center">
              Credits bought on the website show here the next time the app opens.
            </p>
          </>
        ) : (
          <>
            <div className="space-y-2">
              {packs.map((p) => (
                <button
                  key={p.productId}
                  type="button"
                  onClick={() => setPackId(p.productId)}
                  className={
                    'flex w-full items-baseline justify-between border px-4 py-3 text-left ' +
                    (pack?.productId === p.productId
                      ? 'border-gold bg-[rgba(176,141,63,0.08)]'
                      : 'border-border')
                  }
                >
                  <span>
                    <span className="block text-[0.95rem] text-ink">{p.name}</span>
                    <span className="eyebrow">{p.credits} credits · never expire</span>
                  </span>
                  <span className="font-display text-ink">{fmtPeso(p.price)}</span>
                </button>
              ))}
            </div>
            {pack && (
              <>
                <Eyebrow className="mb-3 mt-8">How would you like to pay?</Eyebrow>
                {payable.length === 0 ? (
                  <p className="text-[0.93rem] text-ink/70">
                    No payment method is available right now. Please check back soon.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {payable.map((m) => (
                      <MethodOption
                        key={m.code}
                        method={m}
                        selected={m.code === method?.code}
                        onSelect={() => setMethodCode(m.code)}
                      />
                    ))}
                  </div>
                )}
                {error !== null && <ErrorNote error={error} />}
                <CoachButton
                  kind="gold"
                  block
                  className="mt-6"
                  disabled={!method || buy.isPending}
                  onClick={() => {
                    setError(null);
                    buy.mutate();
                  }}
                >
                  {buy.isPending ? 'Starting…' : `Pay ${fmtPeso(pack.price)}`}
                </CoachButton>
              </>
            )}
          </>
        )}
      </section>

      <section className="mt-10">
        <Eyebrow className="mb-3">History</Eyebrow>
        {entries.isPending ? (
          <p className="eyebrow">Loading…</p>
        ) : (entries.data ?? []).length === 0 ? (
          <p className="text-[0.93rem] text-ink/70">Nothing yet.</p>
        ) : (
          <ul className="divide-y divide-border border-y border-border">
            {(entries.data ?? []).map((e) => (
              <li key={e.id} className="flex items-baseline justify-between gap-4 py-2.5">
                <span>
                  <span className="block text-[0.93rem] text-ink/85">{entryLabel(e)}</span>
                  <span className="eyebrow">{instantDay(e.createdAt)}</span>
                </span>
                <span
                  className={'font-display ' + (e.direction === 'C' ? 'text-sage' : 'text-ink/70')}
                >
                  {e.direction === 'C' ? '+' : '−'}
                  {e.amount}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
      <p className="eyebrow mt-10 text-center">
        <Link to="/profile" className="underline underline-offset-4">
          Back to your profile
        </Link>
      </p>
    </div>
  );
}
